import React, { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Alert,
  Divider,
  List,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { notifications } from '@mantine/notifications';
import { PantryVoiceRecorder } from './PantryVoiceRecorder';
import { importPantryFromVoice } from '../../services/pantryService';

interface ImportPantryVoiceModalProps {
  opened: boolean;
  onClose: () => void;
  onImportStarted: () => void;
}

interface ImportState {
  audio: Blob | null;
  error: string | null;
  importing: boolean;
}

export const ImportPantryVoiceModal: React.FC<ImportPantryVoiceModalProps> = ({
  opened,
  onClose,
  onImportStarted,
}) => {
  const [state, setState] = useState<ImportState>({
    audio: null,
    error: null,
    importing: false,
  });
  const { user } = useAuth();

  const handleRecordingComplete = useCallback((audio: Blob) => {
    setState(prev => ({
      ...prev,
      audio,
      error: null,
    }));
  }, []);

  const handleRecordingCleared = useCallback(() => {
    setState(prev => ({
      ...prev,
      audio: null,
      error: null,
    }));
  }, []);

  const handleReset = useCallback(() => {
    setState({
      audio: null,
      error: null,
      importing: false,
    });
  }, []);

  const handleStartImport = useCallback(async () => {
    if (!user || !state.audio) return;

    setState(prev => ({ ...prev, importing: true, error: null }));

    try {
      const token = await user.getIdToken();
      await importPantryFromVoice(token, state.audio);

      notifications.show({
        title: 'Voice import started',
        message: 'Your recording is being processed. Items will appear at the top when ready for review.',
        color: 'blue',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });

      // Close modal and trigger refresh
      onImportStarted();
      onClose();
      handleReset();

    } catch (error: any) {
      console.error('Pantry voice import error:', error);

      setState(prev => ({
        ...prev,
        importing: false,
        error: error.message || 'Failed to start voice import. Please try again.',
      }));

      notifications.show({
        title: 'Import failed',
        message: error.message || 'Failed to start voice import. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 10000,
      });
    }
  }, [user, state.audio, onImportStarted, onClose, handleReset]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} size="lg">Import from voice recording</Text>}
      size="lg"
      centered
      radius="lg"
      closeOnClickOutside={!state.importing}
      closeOnEscape={!state.importing}
    >
      <Stack gap="md">
        {/* Error Display */}
        {state.error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Import error"
            color="red"
            variant="light"
            radius="lg"
          >
            {state.error}
          </Alert>
        )}

        {/* Voice Recorder */}
        <PantryVoiceRecorder
          onRecordingComplete={handleRecordingComplete}
          onRecordingCleared={handleRecordingCleared}
          disabled={state.importing}
        />

        {/* Instructions */}
        {!state.audio && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="How it works"
            color="blue"
            variant="light"
            radius="lg"
          >
            <List spacing="xs" size="sm">
              <List.Item>Record yourself describing items in your pantry, fridge, or kitchen</List.Item>
              <List.Item>Mention quantities when you know them (e.g. "2 pounds of chicken")</List.Item>
              <List.Item>Our AI will identify items and add them to your pantry</List.Item>
              <List.Item>Imported items will be flagged for your review</List.Item>
            </List>
          </Alert>
        )}

        {/* Import Button */}
        {state.audio && (
          <>
            <Divider />
            <Group justify="flex-end" mt="md">
              <Button
                onClick={handleStartImport}
                loading={state.importing}
                size="md"
                disabled={state.importing || !state.audio}
              >
                {state.importing ? 'Processing Recording...' : 'Import Items'}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};
